import { defineStore } from 'pinia';
import { useRouter } from 'vue-router';
import fastapi from '@/lib/api';
import { useChatState } from '@/composables/useChatState';
import { AUTH_CONSTANTS, TIME_CONST } from '@/constants/auth';
import { decodeToken } from '@/lib/jwtDecode';
import { getCurrentDomainInfo, autoSetTenantIdFromDomain, isFixedTenantDomain, isHeroDomain } from '@/utils/domainMapping';
const HERO_COMPANY_ID = 2;
// 채팅 상태 초기화 함수
function initializeChatState(userEmail) {
  const { currentUser, messages, projectOptions } = useChatState();
  if (currentUser.value !== userEmail) {
    currentUser.value = userEmail;
    //projectOptions.value = null;
    messages.value = [];
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => {
    // 초기에는 저장된 tenantId 사용, 나중에 도메인 기반으로 업데이트
    const storedTenantId = localStorage.getItem('tenantId');
    
    return {
      user: null,
      token: null,
      tokenExpiration: null,
      lastActivityTime:
        parseInt(localStorage.getItem('lastActivityTime')) || null,
      isAuthChecked: false, // 인증 체크가 완료되었는지 확인하는 플래그
      isMonitoringActive: false,
      rememberMe: false,
      loginType: null, // 추가
      //roles: [],
      //permissions: [],
      companyCode: localStorage.getItem('companyCode') || null,
      companyName: localStorage.getItem('companyName') || null,
      tenantId: storedTenantId, // 저장된 값으로 초기화
    };
  },
  getters: {
    isAuthenticated: (state) => {
      const isAuth =
        !!state.token && !!state.user && state.tokenExpiration > Date.now();
      return isAuth;
    },
    userEmail: (state) => state.user?.email,
    userName: (state) => state.user?.name,
    comId: (state) => (isHeroDomain() ? HERO_COMPANY_ID : state.user?.company_id),
    companyId: (state) => (isHeroDomain() ? HERO_COMPANY_ID : state.user?.company_id),
    comName: (state) => state.user?.company_name,
    userId: (state) => state.user?.uid,
    uId: (state) => state.user?.id,
    role: (state) => state.user?.roles,
    permission: (state) => state.user?.permissions,
    hasRole: (state) => (role) => {
      const roleArray =
        state.user?.roles?.split(',').map((r) => r.trim()) || [];
      return roleArray.some((userRole) => userRole === role) || false;
    },

    hasPermission: (state) => (permission) => {
      const permissionArray =
        state.user?.permissions?.split(',').map((p) => p.trim()) || [];
      return (
        permissionArray.some(
          (userPermission) => userPermission === permission,
        ) || false
      );
    },
    hasAnyPermission: (state) => (permissionsOrRoles) => {
      const roleArray =
        state.user?.roles?.split(',').map((r) => r.trim()) || [];
      const permissionArray =
        state.user?.permissions?.split(',').map((p) => p.trim()) || [];

      return (
        permissionsOrRoles.some(
          (item) =>
            roleArray.some((role) => role === item) ||
            permissionArray.some((permission) => permission === item),
        ) || false
      );
    },
    // 벤더사 관련 정보를 위한 getter 추가
    isVendorUser: (state) => state.loginType === 'vendor',
    vendorId: (state) => state.user?.vendor_id,
    vendorName: (state) => state.user?.vendor_name,
    vendorRole: (state) => state.user?.vendor_role,

    // 사용자 타입에 따른 조건부 getter
    currentUserType: (state) => {
      if (!state.loginType) return null;
      return state.loginType === 'vendor' ? 'VENDOR' : 'COMPANY';
    },
  },
  actions: {
    setUserRolesAndPermissions(roles, permissions) {
      this.roles = roles;
      this.permissions = permissions;
    },
    async login(username, password, rememberMe = false, endpoint = '/api/v1/login/') {

      const params = { username, password, remember_me: rememberMe };
      return new Promise((resolve, reject) => {
        // 헤더에 테넌트 ID 직접 추가
        const headers = {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        };

        if (this.tenantId) {
          headers['X-Tenant-ID'] = this.tenantId;
        }

        fastapi(
          'post',
          endpoint,
          params,
          async (json) => {
            if (json && json.access_token) {
              this.setToken(json.access_token, json.expires_in, rememberMe);

              // JWT에서 권한과 역할 정보 추출
              const decodedToken = decodeToken(json.access_token);
              if (decodedToken) {
                if (decodedToken.permissions) {
                  this.permissions = decodedToken.permissions
                    .split(',')
                    .map((item) => item.trim());
                }
                if (decodedToken.roles) {
                  this.roles = decodedToken.roles
                    .split(',')
                    .map((item) => item.trim());
                }
              }

              // 회사 정보 저장
              if (json.company_code && json.company_name) {
                this.setCompanyInfo(json.company_code, json.company_name);
              }

              this.loginType =
                endpoint === '/login/vendor' ? 'vendor' : 'company';
              localStorage.setItem('loginType', this.loginType);
              await this.fetchUserInfo();
              this.rememberMe = rememberMe;

              if (!rememberMe) {
                // 리멤버미가 false일 때만 활동 모니터링 시작
                this.startActivityMonitoring();
              }

              resolve(json);
            } else {
              console.error('Unexpected response structure:', json);
              reject(new Error('Login failed: Invalid response structure'));
            }
          },
          (error) => {
            console.error('Login failed:', error);
            // 에러 객체를 안전하게 전달
            try {
              reject(error);
            } catch (rejectError) {
              console.error('Error in reject:', rejectError);
              // reject 에러가 발생해도 스크립트가 중단되지 않도록 함
            }
          },
          {
            timeout: 10000,
            retries: 0, // 로그인은 재시도하지 않음
            headers: headers // 커스텀 헤더 전달
          },
        );
      });
    },
    setToken(token, expiresIn, rememberMe) {
      this.token = token;
      this.tokenExpiration = Date.now() + expiresIn * 1000;
      this.lastActivityTime = Date.now();
      this.rememberMe = rememberMe;

      if (rememberMe) {
        localStorage.setItem('token', token);
        localStorage.setItem(
          'tokenExpiration',
          this.tokenExpiration.toString(),
        );
        localStorage.setItem(
          'lastActivityTime',
          this.lastActivityTime.toString(),
        );
        localStorage.setItem('rememberMe', 'true');
      } else {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem(
          'tokenExpiration',
          this.tokenExpiration.toString(),
        );
        sessionStorage.setItem(
          'lastActivityTime',
          this.lastActivityTime.toString(),
        );
        localStorage.removeItem('rememberMe');
      }

      const decodedToken = decodeToken(token);
      if (decodedToken && decodedToken.permissions) {
        this.permissions = decodedToken.permissions;
      }
    },
    getToken() {
      if (
        this.token &&
        this.tokenExpiration &&
        Date.now() < this.tokenExpiration
      ) {
        return this.token;
      }
      return null;
    },
    saveToken(newToken, expiresIn) {
      this.token = newToken;
      this.tokenExpiration = Date.now() + expiresIn * 1000;
      this.lastActivityTime = Date.now();
      // 로컬 스토리지에 저장 (선택적)
      localStorage.setItem('token', newToken);
      localStorage.setItem('tokenExpiration', this.tokenExpiration.toString());
      localStorage.setItem(
        'lastActivityTime',
        this.lastActivityTime.toString(),
      );
    },
    clearToken() {
      this.token = null;
      this.tokenExpiration = null;
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiration');
      localStorage.removeItem('rememberMe');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('tokenExpiration');
    },
    setUser(userData) {
      this.user = userData;
    },
    updateLastActivityTime() {
      this.lastActivityTime = Date.now();
      localStorage.setItem(
        'lastActivityTime',
        this.lastActivityTime.toString(),
      );
    },
    async checkAuth() {
      this.loginType = localStorage.getItem('loginType');
      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      const token = rememberMe
        ? localStorage.getItem('token')
        : sessionStorage.getItem('token');
      const tokenExpiration = rememberMe
        ? localStorage.getItem('tokenExpiration')
        : sessionStorage.getItem('tokenExpiration');

      if (token && tokenExpiration) {
        const expirationTime = Number(tokenExpiration);
        if (!isNaN(expirationTime) && expirationTime > Date.now()) {
          this.token = token;
          this.tokenExpiration = expirationTime;
          this.rememberMe = rememberMe;
          try {
            await this.fetchUserInfo();

            if (!rememberMe) {
              // 리멤버미가 false일 때만 활동 모니터링 확인
              this.ensureActivityMonitoring();
            }
          } catch (error) {
            console.error('Failed to fetch user info:', error);
            this.clearAuthData();
          }
        } else {
          this.clearAuthData();
        }
      } else {
        this.clearAuthData();
      }
      this.isAuthChecked = true; // 인증 체크 완료 표시
    },
    async logout(redirectToLogin = false) {
      try {
        await fastapi(
          'post',
          '/login/logout',
          null,
          () => {
            this.performLogout();
          },
          (error) => {
            this.performLogout();
            console.error('Logout failed:', error);
          },
          {
            timeout: 10000,
            retries: 3,
            headers: { Authorization: `Bearer ${this.token}` },
          },
        );
      } catch (error) {
        console.error('Logout error:', error);
        this.performLogout();
      } finally {
        if (redirectToLogin) {
          if (this.router) {
            this.router.push('/auth/login'); // 주입된 router를 사용하여 리디렉션
          } else {
            console.error('Router instance is not available');
          }
        }
      }
    },
    performLogout() {
      this.lastActivityTime = null;
      this.stopAutoLogoutTimer();
      this.stopActivityMonitoring();
      this.clearAuthData();
      this.rememberMe = false;
      
      // clearAuthData에서 이미 tenantId를 보존했으므로 추가 호출 불필요
      // 멀티 테넌트 환경에서 tenantId 유지가 중요함
    },
    // 테넌트 ID 설정
    setTenantId(id) {
      this.tenantId = id;
      if (id) {
        localStorage.setItem('tenantId', id);
      } else {
        localStorage.removeItem('tenantId');
      }
    },
    
    // 도메인 기반으로 tenantId 자동 설정 (URL 경로 우선)
    async setTenantIdFromDomain() {
      // 현재 도메인이 지정된 도메인인지 확인
      const isFixedDomain = await isFixedTenantDomain();
      
      // 지정되지 않은 도메인이면서 로컬스토리지에 tenantId가 있으면 그 값 사용
      if (!isFixedDomain) {
        const storedTenantId = localStorage.getItem('tenantId');
        if (storedTenantId) {
          this.tenantId = storedTenantId;
          return storedTenantId;
        }
      }

      const tenantId = await autoSetTenantIdFromDomain();
      if (tenantId) {
        this.tenantId = tenantId;
      }
      return tenantId;
    },
    
        // 도메인 매핑 정보 가져오기
    async getDomainMappingInfo() {
      try {
        const domainInfo = await getCurrentDomainInfo();
        
        return {
          currentDomain: window.location.hostname,
          currentTenantId: this.tenantId,
          mappedTenantId: domainInfo?.tenant_id || null,
          companyName: domainInfo?.company_name || '공통'
        };
      } catch (error) {
        console.error('도메인 매핑 정보 가져오기 실패:', error);
        return {
          currentDomain: window.location.hostname,
          currentTenantId: this.tenantId,
          mappedTenantId: null,
          companyName: '공통'
        };
      }
    },

    // 테넌트 ID 초기화
    clearTenantId() {
      this.tenantId = null;
      localStorage.removeItem('tenantId');
    },

    // 스토어 초기화 시 도메인 기반 tenantId 설정
    async initializeTenantIdFromDomain() {
      try {
        const domainInfo = await getCurrentDomainInfo();
        if (domainInfo?.tenant_id) {
          this.tenantId = domainInfo.tenant_id.toString();
          localStorage.setItem('tenantId', this.tenantId);
        } else {
          // 도메인 기반 tenantId가 없는 경우 URL 경로에서 추출 시도
          const urlTenantId = this.getTenantIdFromCurrentUrl();
          if (urlTenantId) {
            this.tenantId = urlTenantId.toString();
            localStorage.setItem('tenantId', this.tenantId);
          }
        }
      } catch (error) {
        console.error('스토어 초기화: 도메인 기반 tenantId 설정 실패:', error);
        // 에러 발생 시 URL 경로에서 추출 시도
        const urlTenantId = this.getTenantIdFromCurrentUrl();
        if (urlTenantId) {
          this.tenantId = urlTenantId.toString();
          localStorage.setItem('tenantId', this.tenantId);
        }
      }
    },

    // 로그아웃 시 테넌트 ID는 유지 (멀티 테넌트 환경에서 중요)
    clearAuthData() {
      // tenantId와 테마 관련 설정 임시 저장
      const tempTenantId = localStorage.getItem('tenantId');
      const tempLayoutConfig = localStorage.getItem('layoutConfig');
      const tempLayoutState = localStorage.getItem('layoutState');
      const tempThemeColor = localStorage.getItem('themeColor');
      
      // 현재 스토어의 tenantId도 백업
      const currentStoreTenantId = this.tenantId;
      
      // 인증 관련 데이터만 초기화 (tenantId는 유지)
      this.user = null;
      this.token = null;
      this.tokenExpiration = null;
      this.permissions = [];
      this.clearToken();
      this.loginType = null;
      this.lastActivityTime = null;
      this.isAuthChecked = false;
      
      // 로컬스토리지에서 tenantId와 테마 관련 설정을 제외한 키들만 선택적으로 삭제
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key !== 'tenantId' && 
        key !== 'layoutConfig' && 
        key !== 'layoutState' && 
        key !== 'themeColor'
      );
      keysToRemove.forEach(key => localStorage.removeItem(key));
      sessionStorage.clear();
      
      // 테마 관련 설정 복원
      if (tempLayoutConfig) {
        localStorage.setItem('layoutConfig', tempLayoutConfig);
      }
      if (tempLayoutState) {
        localStorage.setItem('layoutState', tempLayoutState);
      }
      if (tempThemeColor) {
        localStorage.setItem('themeColor', tempThemeColor);
      }
      
      // tenantId 우선순위: localStorage > 스토어 (멀티 테넌트 환경에서 중요)
      let finalTenantId = tempTenantId || currentStoreTenantId;
      
      if (finalTenantId) {
        this.tenantId = finalTenantId;
        localStorage.setItem('tenantId', finalTenantId);
      }
    },
    
    // 현재 URL에서 tenant ID 추출하는 헬퍼 함수
    getTenantIdFromCurrentUrl() {
      try {
        const pathname = window.location.pathname;
        if (!pathname || pathname === '/') {
          return null;
        }
        
        // 인증이 필요 없는 페이지들은 제외
        const publicPaths = ['/auth/login', '/auth/register', '/auth/forgot-password'];
        if (publicPaths.some(path => pathname.startsWith(path))) {
          return null;
        }
        
        // 경로의 첫 번째 세그먼트가 숫자인지 확인
        const pathParts = pathname.split('/').filter(part => part.length > 0);
        if (pathParts.length > 0 && /^\d+$/.test(pathParts[0])) {
          return parseInt(pathParts[0]);
        }
        
        return null;
      } catch (error) {
        console.error('URL에서 tenant ID 추출 실패:', error);
        return null;
      }
    },
    
    async fetchUserInfo() {
      return new Promise((resolve, reject) => {
        if (!this.token) {
          console.error('No token available');
          reject(new Error('Authentication required'));
          return;
        }
        // 로그인 타입에 따라 다른 엔드포인트 사용
        const endpoint =
          this.loginType === 'vendor' ? '/login/vendor/me' : '/login/me';

        // API 호출 헤더 설정
        const headers = { 
          Authorization: `Bearer ${this.token}`
        };
        
        // tenantId가 있는 경우 헤더에 추가
        if (this.tenantId) {
          headers['X-Tenant-ID'] = this.tenantId;
        }

        fastapi(
          'get',
          endpoint,
          null,
          (json) => {
            if (json && (json.uid || json.email)) {
              // company_id와 tenant_id 비교 및 업데이트
              const currentTenantId = localStorage.getItem('tenantId');
              if (json.company_id && currentTenantId !== json.company_id.toString()) {
                localStorage.setItem('tenantId', json.company_id.toString());
              }

              // 채팅 상태 초기화
              initializeChatState(json.email);

              this.setUser(json);
              resolve(json);
            } else {
              console.error('Invalid user data received:', json);
              reject(new Error('Invalid user data'));
            }
          },
          (error) => {
            console.error('Failed to fetch user info:', error);
            if (error.response && error.response.status === 401) {
              this.logout();
            }
            reject(error);
          },
          {
            headers: headers
          },
        );
      });
    },
    startAutoLogoutTimer() {
      // 기존 자동 로그아웃 타이머 정리
      if (this.autoLogoutTimer) {
        clearTimeout(this.autoLogoutTimer);
      }

      // 1분마다 체크하는 새로운 타이머 설정
      this.autoLogoutTimer = setInterval(() => {
        if (
          this.isAuthenticated &&
          Date.now() - this.lastActivityTime >
            AUTH_CONSTANTS.AUTO_LOGOUT_TIME_SECONDS * 1000
        ) {
          this.logout(true);
        }
      }, TIME_CONST.MILLISECONDS_IN_MIN1); // 1분마다 체크
    },
    stopAutoLogoutTimer() {
      if (this.autoLogoutTimer) {
        clearInterval(this.autoLogoutTimer);
        this.autoLogoutTimer = null;
      }
    },
    startTokenManagement() {
      const checkInterval = TIME_CONST.MILLISECONDS_IN_MIN1; // 1분마다 체크
      setInterval(() => {
        if (this.isAuthenticated) {
          const timeUntilExpiration = this.tokenExpiration - Date.now();
          if (timeUntilExpiration <= 2 * TIME_CONST.MILLISECONDS_IN_MIN1) {
            // 2분 이하 남음
            this.refreshToken();
          } else if (timeUntilExpiration <= 0) {
            // 만료됨
            this.logout(true);
          }
        }
      }, checkInterval);
    },
    async refreshTokenIfNeeded() {
      const currentTime = Date.now();
      const timeSinceLastActivity = currentTime - this.lastActivityTime;
      const timeUntilExpiration = this.tokenExpiration - currentTime;

      // 마지막 활동 후 25분이 지났고, 토큰 만료 5분 전이라면 리프레시
      if (
        timeSinceLastActivity > 25 * TIME_CONST.MILLISECONDS_IN_MIN1 &&
        timeUntilExpiration < 5 * TIME_CONST.MILLISECONDS_IN_MIN1
      ) {
        await this.refreshToken();
      }
    },
    async refreshToken() {
      const endpoint = '/login/refresh';
      
      return new Promise((resolve, reject) => {
        const headers = {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        };
        
        if (this.tenantId) {
          headers['X-Tenant-ID'] = this.tenantId;
        }

        fastapi(
          'post',
          endpoint,
          null,
          (json) => {
            if (json && json.access_token) {
              this.setToken(json.access_token, json.expires_in, this.rememberMe);
              resolve(json.access_token);
            } else {
              reject(new Error('Invalid refresh token response'));
            }
          },
          (error) => {
            console.error('Token refresh failed:', error);
            this.performLogout();
            reject(error);
          },
          {
            headers: headers,
            timeout: 5000,
            retries: 0
          }
        );
      });
    },
    ensureActivityMonitoring() {
      if (
        !this.isMonitoringActive &&
        this.isAuthenticated &&
        !this.rememberMe
      ) {
        this.startActivityMonitoring();
        this.isMonitoringActive = true;
      }
    },
    startActivityMonitoring() {
      if (this.rememberMe) {
        return;
      }
      // 이벤트 핸들러 함수 정의
      this.activityHandler = () => {
        this.updateLastActivityTime();
        this.refreshTokenIfNeeded();
      };
      // 사용자 활동을 모니터링하는 이벤트 리스너 등록 , 'mousemove'
      ['click', 'keypress', 'scroll'].forEach((eventName) => {
        window.addEventListener(eventName, this.activityHandler);
      });

      // 주기적인 토큰 상태 확인
      this.activityCheckInterval = setInterval(
        () => {
          if (this.isAuthenticated) {
            this.refreshTokenIfNeeded();
          } else {
            this.stopActivityMonitoring();
          }
        },
        2 * TIME_CONST.MILLISECONDS_IN_MIN1, //2분마다 체크
      );

      // 자동 로그아웃 타이머 시작
      this.startAutoLogoutTimer();
      this.isMonitoringActive = true;
    },
    stopActivityMonitoring() {
      // 이벤트 리스너 제거, 'mousemove'
      if (this.activityHandler) {
        ['click', 'keypress', 'scroll'].forEach((eventName) => {
          window.removeEventListener(eventName, this.activityHandler);
        });
      }

      // 인터벌 정리
      if (this.activityCheckInterval) {
        clearInterval(this.activityCheckInterval);
      }

      // 자동 로그아웃 타이머 정리
      if (this.autoLogoutTimer) {
        clearTimeout(this.autoLogoutTimer);
      }

      this.isMonitoringActive = false;
    },
    initializeToken() {
      const storedToken = localStorage.getItem('token');
      const storedExpiration = localStorage.getItem('tokenExpiration');

      if (storedToken && storedExpiration) {
        const expirationTime = parseInt(storedExpiration, 10);
        if (Date.now() < expirationTime) {
          this.token = storedToken;
          this.tokenExpiration = expirationTime;
        } else {
          this.clearToken();
        }
      }
    },
    setCompanyInfo(code, name) {
      this.companyCode = code;
      this.companyName = name;
      localStorage.setItem('companyCode', code);
      localStorage.setItem('companyName', name);
    },

    clearCompanyInfo() {
      this.companyCode = null;
      this.companyName = null;
      localStorage.removeItem('companyCode');
      localStorage.removeItem('companyName');
    },
  },
});
